import React, { useEffect } from "react";
import { isMobile, isTablet } from "react-device-detect";
import styled from "styled-components";
import StackedButton from "@common/LifeAtButton/Variants/StackedButton";
import LifeAtVolumeSlider from "@common/LifeAtVolumeSlider/LifeAtVolumeSlider";
import useMediaDevices from "@hooks/useMediaDevices";
import CameraOffIcon from "@icons/Rooms/CamOff.svg";
import CameraOnIcon from "@icons/Rooms/CamOn.svg";
import MicOffIcon from "@icons/Rooms/MicOff.svg";
import MicOnIcon from "@icons/Rooms/MicOn.svg";
import ShareScreenIcon from "@icons/Rooms/ScreenShare.svg";
import Styles from "@themes/Styles";
import { RoomControls } from "../../RoomController";
import { useRoomSettingsContext } from "../../RoomSettingsContext";
import { useVideoChatContext } from "../../VideoChatContext";
import InputDeviceControl from "../InputDeviceControl";

const FooterWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    height: 100%;
    width: 100%;
`;

const RoomVolumeSliderWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    width: 50%;
`;

const RoomParticipantVolumeSlider = styled(LifeAtVolumeSlider)`
    min-width: 50%; // Widget.tsx min-width is 180px (see Widget.scss)
    padding: 6px;
`;

const VideoChatControls = styled.div`
    width: 50%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 3px;
`;

const StyledStackedButton = styled(StackedButton)<{ isActive?: boolean }>`
    min-height: ${Styles.overlayButtonHeight};
    height: ${Styles.overlayButtonHeight};
    max-height: ${Styles.overlayButtonHeight};
    width: 40px;
    margin: unset;
`;

const StyledInputDeviceControl = styled(InputDeviceControl)`
    min-height: ${Styles.overlayButtonHeight};
    height: ${Styles.overlayButtonHeight};
    max-height: ${Styles.overlayButtonHeight};
`;

const StyledMicOnIcon = styled(MicOnIcon)`
    fill: ${({ theme }) => theme.textColorPrimary};
`;

const StyledMicOffIcon = styled(MicOffIcon)`
    fill: ${({ theme }) => theme.textColorPrimary};
`;

const StyledCameraOnIcon = styled(CameraOnIcon)`
    fill: ${({ theme }) => theme.textColorPrimary};
`;

const StyledCameraOffIcon = styled(CameraOffIcon)`
    fill: ${({ theme }) => theme.textColorPrimary};
`;

const CANNOT_SHARE_SCREEN_TOOLTIP_TEXT = "Someone else is currently sharing screen";
const CAM_BTN_LABEL = "Cam";
const SHARE_SCREEN_START_BTN_LABEL = "Share";
const SHARE_SCREEN_STOP_BTN_LABEL = "Stop";
const MIC_BTN_LABEL = "Mic";
const LEAVE_CALL_BTN_LABEL = "Leave";

interface VideoChatWidgetFooterProps {
    readonly roomVolume: number;
    readonly setRoomVolume: (newVol: number) => void;
    readonly roomController: RoomControls;
}
export default function VideoChatWidgetFooter(props: VideoChatWidgetFooterProps) {
    const { isGuestsCameraDisabled, isGuestsMicDisabled, isScreenShareDisabled, isVideoCallingDisabled } =
        useRoomSettingsContext();

    const {
        isMyCameraOn,
        setIsMyCameraOn,
        setSelectedCameraDevice,
        isMyMicOn,
        setIsMyMicOn,
        setSelectedMicDevice,
        isMyScreenShared,
        setIsMyScreenShared,
    } = useVideoChatContext();

    // If the user is a guest and the host disables video calling, we want to
    // remove the user from the call.
    useEffect(() => {
        if (isVideoCallingDisabled) {
            handleDisconnectCall();
        }
    }, [isVideoCallingDisabled]);

    const handleToggleScreenShare = () => {
        if (!props.roomController.isRoomScreenSharing || isMyScreenShared) {
            setIsMyScreenShared(!isMyScreenShared);
        }
    };

    const handleAudioInputSelect = (device: MediaDeviceInfo) => {
        if (device && !audioDevices.includes(device)) {
            if (!audioDevices[0]) {
                alert("No audio devices found for microphone!");
            } else {
                selectAudioInput(audioDevices[0]);
            }
        }
    };

    const handleVideoInputSelect = (device: MediaDeviceInfo) => {
        if (device && !videoDevices.includes(device)) {
            if (!videoDevices[0]) {
                alert("No video devices found for camera!");
            } else {
                selectVideoInput(videoDevices[0]);
            }
        }
    };

    const { audioDevices, selectedAudioInput, selectAudioInput, videoDevices, selectedVideoInput, selectVideoInput } =
        useMediaDevices(handleAudioInputSelect, handleVideoInputSelect);

    useEffect(() => {
        if (selectedAudioInput) {
            setSelectedMicDevice(selectedAudioInput);
        }
    }, [selectedAudioInput]);

    useEffect(() => {
        if (selectedVideoInput) {
            setSelectedCameraDevice(selectedVideoInput);
        }
    }, [selectedVideoInput]);

    const handleToggleMic = () => {
        if (!selectedAudioInput) {
            const defaultMic = audioDevices[0];
            if (defaultMic != null) {
                selectAudioInput(defaultMic);
            }
        }
        setIsMyMicOn(!isMyMicOn);
    };

    const handleToggleCam = () => {
        if (!selectedVideoInput) {
            const defaultCamera = videoDevices[0];
            if (defaultCamera != null) {
                selectVideoInput(defaultCamera);
            }
        }
        setIsMyCameraOn(!isMyCameraOn);
    };

    const toggleCamButton = (
        <StyledStackedButton
            action={"click"}
            handleClick={handleToggleCam}
            label={CAM_BTN_LABEL}
            icon={isMyCameraOn ? <StyledCameraOnIcon /> : <StyledCameraOffIcon />}
        />
    );

    const camSelectionButton = (
        <StyledInputDeviceControl
            icon={isMyCameraOn ? <StyledCameraOnIcon /> : <StyledCameraOffIcon />}
            label={CAM_BTN_LABEL}
            onToggle={handleToggleCam}
            onSelect={selectVideoInput}
            devices={null}
            selectedDevice={selectedVideoInput}
        />
    );

    const micSelectionButton = (
        <StyledInputDeviceControl
            icon={isMyMicOn ? <StyledMicOnIcon /> : <StyledMicOffIcon />}
            label={MIC_BTN_LABEL}
            onToggle={handleToggleMic}
            onSelect={selectAudioInput}
            devices={null}
            selectedDevice={selectedAudioInput}
        />
    );

    const toggleMicButton = (
        <StyledStackedButton
            action={"click"}
            handleClick={handleToggleMic}
            label={MIC_BTN_LABEL}
            icon={isMyMicOn ? <StyledMicOnIcon /> : <StyledMicOffIcon />}
        />
    );

    // NOTE: This is temporary. Clean this up as part of LAS-1712
    const cameraToggleButton = true ? toggleCamButton : camSelectionButton;
    const micToggleButton = true ? toggleMicButton : micSelectionButton;

    const roomVideoChatControl = (
        <VideoChatControls>
            {/* Screen Share Button */}
            {!isScreenShareDisabled && !(isMobile || isTablet) && (
                <StyledStackedButton
                    label={isMyScreenShared ? SHARE_SCREEN_STOP_BTN_LABEL : SHARE_SCREEN_START_BTN_LABEL}
                    icon={<ShareScreenIcon />}
                    border={false}
                    action={"click"}
                    handleClick={handleToggleScreenShare}
                    disabled={props.roomController.isRoomScreenSharing && !isMyScreenShared}
                    disabledTooltipText={CANNOT_SHARE_SCREEN_TOOLTIP_TEXT}
                    isActive={isMyScreenShared}
                />
            )}

            {/* Toggle Webcam Button */}
            {!isGuestsCameraDisabled && cameraToggleButton}

            {/* Toggle Mic Button*/}
            {!isGuestsMicDisabled && micToggleButton}
        </VideoChatControls>
    );

    const roomVolumeControl = (
        <RoomVolumeSliderWrapper>
            <RoomParticipantVolumeSlider volume={props.roomVolume} setVolume={props.setRoomVolume} />
        </RoomVolumeSliderWrapper>
    );

    const handleDisconnectCall = () => {
        setIsMyMicOn(false);
        setIsMyCameraOn(false);
        setIsMyScreenShared(false);
        props.roomController.leaveRoomCall();
    };

    const leaveCallButton = (
        <StackedButton
            label={LEAVE_CALL_BTN_LABEL}
            border={false}
            variant={"red"}
            action={"click"}
            handleClick={handleDisconnectCall}
        />
    );

    return (
        <FooterWrapper>
            {roomVideoChatControl}
            {roomVolumeControl}
            {leaveCallButton}
        </FooterWrapper>
    );
}
